import React from 'react';
import styled from 'styled-components';
import useTranslation from '@src/hooks/useTranslation';
import { useSelector } from 'react-redux';
import _ from 'lodash';

import useCurrentCourse from '@src/hooks/useCurrentCourse';
import TeacherSaid from '@src/components/course/basicInfo/courseCard/courseDetail/teacherSaid';
import { isPublished, isFundraisingRefund } from '@src/utils/course';
import { getIsTagVisible } from '@src/utils/hardcode';
import * as selectors from '@src/store/selectors';

import Avatar from './avatar';

const CourseMainWrapper = styled.div`
	width: 100%;
`;

const Title = styled.div`
	display: -webkit-box;
	text-overflow: ellipsis;
	overflow: hidden;
	-webkit-line-clamp: 4;
	-webkit-box-orient: vertical;
	font-size: 24px;
	font-weight: 500;
	color: #101010;

	@media ${props => props.theme.device.mobile} {
		font-size: 22px;
	}
`;

const Teacher = styled.div`
	display: flex;
	align-items: center;
	margin-top: 16px;
`;

const TeacherInfo = styled.div`
	margin-left: 12px;
`;

const TeacherName = styled.div`
	margin-bottom: 4px;
	font-size: 18px;
	font-weight: 500;
	color: #101010;

	@media ${props => props.theme.device.mobile} {
		font-size: 18px;
	}
`;

const TagsGroup = styled.div`
	display: flex;
	margin-bottom: 8px;

	& > *:not(:first-child) {
		margin-left: 8px;
	}
`;

const Tag = styled.div`
	border: 1px solid #DDD;
	border-radius: 4px;
	padding: 4px 8px;
	color: #101010;
  font-size: 12px;
`;

const TeacherSaidWrapper = styled.div`
	@media
		${props => props.theme.device.tablet},
		${props => props.theme.device.smTablet},
		${props => props.theme.device.mobile}
	{
		padding-bottom: 16px;
		border-bottom: 1px solid #F0F0F0;
	}
`;

const TeacherWrapper = styled.div`
	margin-left: -10px;
`;

const NumberContent = styled.div`
	background: #DDDDDD;
	font-size: 14px;
	color: #333333;
	width: 36px;
	height: 36px;
	border-radius: 100px;
	display: flex;
	justify-content: center;
	align-items: center;
`;

interface ICourseMain {
	className?: string;
}

const CourseMain = ({ className }: ICourseMain): JSX.Element => {
	const { t } = useTranslation();
	const {
		currentCourse: {
			title,
			teacher,
			public: isPublic,
			status,
			paid,
			extra_teachers: extraTeachers,
		},
		isPreview,
	} = useCurrentCourse();
	const showTeacherSaid = (isPublished(status) && !paid) || isFundraisingRefund(status);
	const domain = useSelector(selectors.getDomain) as string;
	const isTagVisible = getIsTagVisible(domain);
	const teachers = [teacher, ...(extraTeachers || [])];
	return (
		<CourseMainWrapper className={className}>
			<TagsGroup>
				{/* public might be undefined */}
				{!(isPublic !== false) && <Tag>{t('common:non_public')}</Tag>}
				{isTagVisible && (<Tag>{t('course:course_products')}</Tag>)}
			</TagsGroup>
			<Title>{title}</Title>
			<Teacher>
				{
					([...teachers].splice(0, 3)).map((item, index) => (
						<TeacherWrapper key={item.id} style={{ zIndex: teachers.length - index }}>
							<Avatar
								avatar={_.isString(item?.cover_link) ? item?.cover_link : item?.cover_link?.link}
								width={44}
							/>
						</TeacherWrapper>
					))
				}
				{
					teachers.length > 3 && (
						<TeacherWrapper>
							<Avatar
								avatar={teacher.cover_link.link}
								width={44}
								component={<NumberContent>{`+${teachers.length - 3}`}</NumberContent>}
							/>
						</TeacherWrapper>
					)
				}
				{
					teachers.length < 4 && (
						<TeacherInfo>
							<TeacherName>{`${teachers.map(item => item.name).join('、')}`}</TeacherName>
						</TeacherInfo>
					)
				}
			</Teacher>
			{(showTeacherSaid || isPreview) && (
				<TeacherSaidWrapper>
					<TeacherSaid />
				</TeacherSaidWrapper>
			)}
		</CourseMainWrapper>
	);
};

export default CourseMain;
